// Permission-gating helpers for the frontend RBAC UI.
//
// Source of truth: ``GET /api/v1/users/me/permissions/`` — returns the user's
// effective grants (broadest scope per code, post-union across roles). The
// backend is authoritative; these helpers are UI-only convenience.

import { useMemo } from "react";

import type { EffectiveGrant, Scope } from "@/lib/api/rbac";
import { useMyPermissionsQuery } from "@/lib/hooks/rbac";
import { useAuthStore } from "@/lib/auth/store";

// Mirrors the backend breadth ordering (apps/accounts/permissions/scope.py):
// tenant > branch > assigned == own_members. `own_members` must be present or
// the minScope comparison reads `undefined` for own_members-scoped grants.
const SCOPE_BREADTH: Record<Scope, number> = {
  tenant: 3,
  branch: 2,
  assigned: 1,
  own_members: 1,
};

interface PermissionsState {
  grants: EffectiveGrant[];
  byCode: Record<string, Scope>;
  isLoading: boolean;
  isError: boolean;
}

export function useEffectivePermissions(): PermissionsState {
  const { data, isLoading, isError } = useMyPermissionsQuery();
  return useMemo(() => {
    const grants = data ?? [];
    const byCode: Record<string, Scope> = {};
    for (const g of grants) byCode[g.code] = g.scope;
    return { grants, byCode, isLoading, isError };
  }, [data, isLoading, isError]);
}

/** True if the user has ``code`` at any scope; if ``minScope`` given, requires
 * the user's scope for ``code`` to be at least as broad as ``minScope``.
 * Superusers always return true.
 */
export function useHasPermission(code: string, minScope?: Scope): boolean {
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser ?? false);
  const { byCode } = useEffectivePermissions();
  if (isSuperuser) return true;
  const userScope = byCode[code];
  if (!userScope) return false;
  if (!minScope) return true;
  return SCOPE_BREADTH[userScope] >= SCOPE_BREADTH[minScope];
}
